import { useState } from "react";
import { supabase } from "../supabase";
import { C } from "../lib/constants";
import { clearFicioStorage } from "../lib/helpers";
import { Btn } from "./ui/Btn";
import { Inp } from "./ui/Inp";
import { TopNav } from "./ui/TopNav";

// Registration gate: returns whether an Auth user has real rows in the public schema.
// A Supabase Auth session alone does NOT grant FICIO client/worker access.
const verificarRegistro = async (userId) => {
  if (!userId) return { registered: false, hasCliente: false, hasTrabajador: false, perfil: null, cliente: null, trabajador: null };
  const [perfilRes, clienteRes, trabajadorRes] = await Promise.all([
    supabase.from("perfiles").select("*").eq("user_id", userId).neq("archivado", true).maybeSingle(),
    supabase.from("clientes").select("*").eq("user_id", userId).maybeSingle(),
    supabase.from("trabajadores").select("*").eq("user_id", userId).neq("archivado", true).maybeSingle(),
  ]);
  return {
    registered: Boolean(perfilRes.data || clienteRes.data || trabajadorRes.data),
    hasCliente: Boolean(clienteRes.data),
    hasTrabajador: Boolean(trabajadorRes.data),
    perfil: perfilRes.data || null,
    cliente: clienteRes.data || null,
    trabajador: trabajadorRes.data || null,
  };
};

// ─── PORTAL CLIENTE FICIO ────────────────────────────────────────────────────
// Una sola pantalla de autenticación para ambos roles
// rol: "cliente" | "trabajador"
export const FicioAuthScreen = ({setScreen, onAuthReady, rol="cliente"}) => {
  const [tab,            setTab]            = useState("login");
  const [correo,         setCorreo]         = useState("");
  const [password,       setPassword]       = useState("");
  const [confirmPass,    setConfirmPass]    = useState("");
  const [nombre,         setNombre]         = useState("");
  const [telefono,       setTelefono]       = useState("");
  const [loading,        setLoading]        = useState(false);
  const [err,            setErr]            = useState("");
  const [msg,            setMsg]            = useState("");
  const [loginError,     setLoginError]     = useState(null);

  const esWorker = rol === "trabajador";
  const color    = esWorker ? C.accent : C.blue;
  const colorD   = esWorker ? C.accentD : "#123E73";

  const limpiar = () => { setErr(""); setMsg(""); setLoginError(null); };

  // Traducir mensajes de error de Supabase Auth al español
  const traducirError = (msg="") => {
    if (msg.includes("already registered") || msg.includes("User already registered"))
      return "Este correo ya tiene una cuenta en Ficio. Usa 'Iniciar sesión'.";
    if (msg.includes("Invalid login credentials") || msg.includes("invalid_credentials"))
      return "No encontramos una cuenta vinculada a FICIO con ese correo. Verifica tus datos o regístrate para crear una cuenta.";
    if (msg.includes("Email not confirmed"))
      return "Confirma tu correo antes de iniciar sesión. Revisa tu bandeja de entrada.";
    if (msg.includes("Password should be at least"))
      return "La contraseña debe tener al menos 6 caracteres.";
    if (msg.includes("Unable to validate email"))
      return "El correo no es válido. Revísalo.";
    if (msg.includes("rate limit") || msg.includes("too many requests"))
      return "Demasiados intentos. Espera un momento e intenta de nuevo.";
    if (msg.includes("network") || msg.includes("fetch"))
      return "Error de conexión. Verifica tu internet e intenta de nuevo.";
    return msg;
  };

  const crearCuenta = async () => {
    limpiar();
    if (!nombre.trim())               return setErr("Escribe tu nombre completo.");
    if (!telefono.trim())             return setErr("Escribe tu número de WhatsApp.");
    const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo.trim());
    if (!correo.trim())               return setErr("Escribe tu correo electrónico.");
    if (!emailValido)                 return setErr("El correo no tiene un formato válido.");
    if (password.length < 6)         return setErr("La contraseña debe tener mínimo 6 caracteres.");
    if (password !== confirmPass)     return setErr("Las contraseñas no coinciden. Verifica.");
    setLoading(true);

    const cleanPhone = telefono.replace(/\D/g, "");
    const emailLimpio = correo.trim().toLowerCase();
    const { data, error } = await supabase.auth.signUp({
      email: emailLimpio,
      password,
      options: { data: { nombre: nombre.trim(), telefono: cleanPhone, role: rol, rol } },
    });

    let userId = null;

    if (error) {
      const isAlreadyReg = error.message?.includes("already registered") || error.message?.includes("User already registered");
      if (!isAlreadyReg) { setErr(traducirError(error.message)); setLoading(false); return; }
      // Email exists in Auth — sign in to verify whether a FICIO profile row exists
      const { data: siData, error: siError } = await supabase.auth.signInWithPassword({ email: emailLimpio, password });
      if (siError) { setErr("Este correo ya tiene una cuenta en FICIO. Usa 'Iniciar sesión'."); setLoading(false); return; }
      const { data: perfilRow } = await supabase.from("perfiles").select("id").eq("user_id", siData.user?.id).maybeSingle();
      if (perfilRow) {
        // Fully registered FICIO user — block
        await supabase.auth.signOut();
        setErr("Este correo ya tiene una cuenta registrada en FICIO. Usa 'Iniciar sesión'.");
        setLoading(false);
        return;
      }
      // Ghost user: Auth account exists but no FICIO profile — allow registration
      userId = siData.user?.id;
    } else {
      userId = data?.user?.id;
    }
    if (userId) {
      // Crear perfil base en tabla perfiles
      await supabase.from("perfiles").upsert([Object.fromEntries(
        Object.entries({
          user_id:     userId,
          nombre:      nombre.trim()               || undefined,
          telefono:    cleanPhone                  || undefined,
          role:        rol,
          rol:         rol,
          email:       correo.trim().toLowerCase() || undefined,
          correo:      correo.trim().toLowerCase() || undefined,
          modo_activo: rol,
          updated_at:  new Date().toISOString(),
        }).filter(([, v]) => v !== undefined && v !== null && v !== "")
      )], { onConflict: "user_id" });

      // Si es cliente: crear registro inicial en tabla clientes
      // NOTA: sin "reputacion" (columna no existe en schema base)
      if (!esWorker) {
        await supabase.from("clientes").upsert([{
          user_id: userId,
          nombre: nombre.trim(),
          telefono: cleanPhone,
          correo: correo.trim().toLowerCase(),
          email: correo.trim().toLowerCase(),
          status: "activo",
          calificacion: 5,
          trabajos: 0,
          verificado: false,
          ultimo_acceso: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }], { onConflict: "user_id" });
      }
    }

    setLoading(false);
    // FIX AUDIT: onAuthReady primero, luego setScreen — evita condición de carrera
    // donde onAuthStateChange y setScreen compiten por la misma pantalla
    await onAuthReady?.();
    setLoading(false);
    // SIGNED_IN en onAuthStateChange ya redirige a obra-activa para clientes.
    // Para trabajadores, redirigir a reg-worker para completar el perfil.
    if (esWorker) setScreen("reg-worker");
    // Cliente: la redirección la maneja onAuthStateChange(SIGNED_IN)
  };

  const entrar = async () => {
    limpiar();
    const raw = correo.trim();
    if (!raw)      return setErr("Escribe tu correo electrónico.");
    if (!password) return setErr("Escribe tu contraseña.");

    const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
    if (!emailValido) return setErr("Escribe un correo electrónico válido.");

    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({ email: raw.toLowerCase(), password });

    if (error) { setErr(traducirError(error.message)); setLoading(false); return; }

    // Block ghost accounts: Auth session is not proof of FICIO registration.
    const { data: freshAuth } = await supabase.auth.getUser();
    const resultado = await verificarRegistro(freshAuth?.user?.id);
    if (!resultado.registered) {
      await supabase.auth.signOut();
      clearFicioStorage();
      setLoginError(true);
      setLoading(false);
      return;
    }
    if (esWorker && !resultado.hasTrabajador) {
      await supabase.auth.signOut();
      clearFicioStorage();
      setErr("Esta cuenta no tiene perfil de maestro. Entra desde 'Soy Cliente' o regístrate como maestro.");
      setLoading(false);
      return;
    }

    await onAuthReady?.();
    setLoading(false);
    // Navigation is handled by onAuthStateChange(SIGNED_IN).
  };

  const passwordOk = password.length >= 6;
  const coinciden  = password === confirmPass && confirmPass.length > 0;

  return (
    <div style={{minHeight:"100vh",background:C.bg,paddingBottom:100}}>
      <TopNav setScreen={setScreen} back="landing" backLabel="Inicio"
        title={esWorker ? "Portal del Maestro" : "Portal del Cliente"}/>
      <div style={{maxWidth:520,margin:"0 auto",padding:"20px"}}>

        {/* Header */}
        <div style={{background:`linear-gradient(135deg,${color},${colorD})`,borderRadius:24,padding:24,color:"#fff",marginBottom:18,boxShadow:`0 8px 28px ${color}40`}}>
          <div style={{fontSize:40,marginBottom:8}}>{esWorker?"🔨":"🏠"}</div>
          <div style={{fontFamily:"Georgia,serif",fontSize:26,fontWeight:900,marginBottom:5}}>
            {esWorker?"Portal del Maestro":"Portal del Cliente"}
          </div>
          <div style={{fontSize:13,opacity:.88,lineHeight:1.55}}>
            {esWorker
              ? "Entra para revisar solicitudes, administrar tus trabajos y mantener tu perfil activo."
              : "Entra para buscar maestros, gestionar tus obras y ver tu historial."}
          </div>
        </div>

        {/* Card principal */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,.04)",marginBottom:14}}>

          {/* Tabs */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
            <button onClick={()=>{setTab("login");limpiar();setConfirmPass("");}}
              style={{border:"none",borderRadius:12,padding:"12px",background:tab==="login"?color:C.bg,color:tab==="login"?"#fff":C.sub,fontWeight:900,cursor:"pointer",fontSize:14,transition:"all 0.2s"}}>
              Iniciar sesión
            </button>
            <button onClick={()=>{setTab("register");limpiar();}}
              style={{border:"none",borderRadius:12,padding:"12px",background:tab==="register"?color:C.bg,color:tab==="register"?"#fff":C.sub,fontWeight:900,cursor:"pointer",fontSize:14,transition:"all 0.2s"}}>
              Crear cuenta
            </button>
          </div>

          {/* Campos solo en registro */}
          {tab==="register" && (
            <>
              <Inp label="Nombre completo" placeholder="Como aparece en tu INE" value={nombre} onChange={e=>setNombre(e.target.value)} icon="👤"/>
              <Inp label="WhatsApp" type="tel" placeholder="10 dígitos sin espacios" value={telefono} onChange={e=>setTelefono(e.target.value)} icon="📱"/>
            </>
          )}

          {/* Correo/teléfono y contraseña — siempre visibles */}
          <Inp label="Correo electrónico" type="email" placeholder="correo@ejemplo.com" value={correo} onChange={e=>setCorreo(e.target.value)} icon="✉️"/>
          <Inp label="Contraseña" type="password" placeholder="Mínimo 6 caracteres" value={password} onChange={e=>setPassword(e.target.value)} icon="🔒"/>

          {/* Confirmar contraseña — solo en registro */}
          {tab==="register" && (
            <>
              <Inp label="Confirmar contraseña" type="password" placeholder="Repite tu contraseña" value={confirmPass} onChange={e=>setConfirmPass(e.target.value)} icon="🔒"/>
              {/* Indicadores visuales en tiempo real */}
              <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:8,paddingLeft:2}}>
                {password.length > 0 && (
                  <div style={{fontSize:11,color:passwordOk?C.green:C.red,fontWeight:700}}>
                    {passwordOk?"✓ Contraseña con longitud válida":"⚠ Mínimo 6 caracteres"}
                  </div>
                )}
                {confirmPass.length > 0 && (
                  <div style={{fontSize:11,color:coinciden?C.green:C.red,fontWeight:700}}>
                    {coinciden?"✓ Las contraseñas coinciden":"⚠ Las contraseñas no coinciden"}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Mensajes de error y éxito */}
          {loginError && (
            <div style={{background:C.redL,border:`1px solid ${C.red}30`,borderRadius:12,padding:"14px 16px",margin:"10px 0"}}>
              <div style={{color:C.red,fontSize:13,fontWeight:700,marginBottom:12,lineHeight:1.5}}>
                Esta cuenta aún no está registrada en FICIO. Regístrate para continuar.
              </div>
              <Btn full color={color} onClick={() => setScreen(esWorker ? "reg-worker" : "reg-client")}>
                Registrarme
              </Btn>
            </div>
          )}
          {err && (
            <div style={{background:C.redL,border:`1px solid ${C.red}30`,borderRadius:12,padding:"10px 12px",color:C.red,fontSize:12,margin:"10px 0",fontWeight:600}}>
              {err}
            </div>
          )}
          {msg && (
            <div style={{background:C.greenL,border:`1px solid ${C.green}30`,borderRadius:12,padding:"10px 12px",color:C.green,fontSize:12,margin:"10px 0"}}>
              {msg}
            </div>
          )}

          <div style={{marginTop:6}}>
            <Btn full color={color} onClick={tab==="login"?entrar:crearCuenta}>
              {loading ? "Procesando..." : tab==="login" ? "Entrar →" : `Crear cuenta ${esWorker?"de Maestro":"de Cliente"} →`}
            </Btn>
          </div>

          {/* Enlace para cambiar de tab */}
          {tab==="login" && (
            <div style={{textAlign:"center",marginTop:12,fontSize:12,color:C.muted}}>
              ¿No tienes cuenta?{" "}
              <span onClick={()=>{setTab("register");limpiar();}} style={{color,fontWeight:700,cursor:"pointer"}}>
                Crear cuenta gratis
              </span>
            </div>
          )}
          {tab==="register" && (
            <div style={{textAlign:"center",marginTop:12,fontSize:12,color:C.muted}}>
              ¿Ya tienes cuenta?{" "}
              <span onClick={()=>{setTab("login");limpiar();setConfirmPass("");}} style={{color,fontWeight:700,cursor:"pointer"}}>
                Iniciar sesión
              </span>
            </div>
          )}
        </div>

        {/* Qué puede hacer */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:16,boxShadow:"0 2px 6px rgba(0,0,0,.04)"}}>
          <div style={{fontFamily:"Georgia,serif",fontSize:16,fontWeight:800,color:C.text,marginBottom:10}}>
            {esWorker?"¿Qué podrás hacer?":"¿Qué puedes hacer?"}
          </div>
          {(esWorker
            ? ["Recibir solicitudes de clientes","Ver y gestionar tus obras activas","Aparecer en el directorio verificado","Acceder al Fondo de Contingencia","Cobrar con Mercado Pago o efectivo"]
            : ["Buscar maestros por oficio y zona","Solicitar cotizaciones y obras","Ver el historial de tus trabajos","Calificar a los maestros","Solicitar mediación si hay problemas"]
          ).map(t=>(
            <div key={t} style={{display:"flex",gap:8,marginBottom:7,alignItems:"flex-start"}}>
              <span style={{color,fontWeight:700,flexShrink:0}}>✓</span>
              <span style={{color:C.sub,fontSize:13}}>{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Alias para compatibilidad con el router existente
export const WorkerLoginScreen  = ({setScreen, onAuthReady}) => <FicioAuthScreen setScreen={setScreen} onAuthReady={onAuthReady} rol="trabajador"/>;
export const ClienteLoginScreen = ({setScreen, onAuthReady}) => <FicioAuthScreen setScreen={setScreen} onAuthReady={onAuthReady} rol="cliente"/>;
